import { useState, useCallback, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { 
  login, logout, resetUserFetchAttempt, clearError,
  fetchCurrentUser, loginThunk, logoutThunk, register, googleLoginThunk
} from '../store/slices/authSlice';
import { 
  getStoredToken, 
  getStoredUser, 
  storeAuthData, 
  clearAuthData, 
  isTokenValid, 
  TOKEN_STORAGE_KEY,
  USER_STORAGE_KEY
} from '../utils/auth';
import { loginUser, getCurrentUser } from '../api/auth';

// Define the missing types if they don't exist
interface LoginRequest {
  username: string;
  password: string;
}

interface RegisterRequest {
  username: string;
  email: string;
  password: string;
  // Add any other fields required for registration
}

export const useAuth = () => {
  const dispatch = useAppDispatch();
  const { user, token, isAuthenticated, loading, error, userFetchAttempted } = useAppSelector(state => state.auth);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Initialize auth state from localStorage on app startup
  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = getStoredToken();
      const storedUser = getStoredUser();

      // Debug
      console.log('Initializing auth state');
      console.log('Stored token exists:', !!storedToken);
      console.log('Stored user exists:', !!storedUser);
      
      if (storedToken && storedUser) {
        // Always update the Redux state with stored data on startup
        console.log('Restoring auth state from localStorage');
        dispatch(login({ token: storedToken, user: storedUser }));
        
        // If token is invalid, we'll clear it later after we get 401 from API
        if (!isTokenValid(storedToken)) {
          console.log('Token appears expired, will validate with server');
        }
      } else if (storedToken && !storedUser) {
        // Token exists but user data is missing, try to fetch user
        console.log('Token exists but user data missing, will fetch user data');
        dispatch(resetUserFetchAttempt());
      } else {
        // No valid auth data
        console.log('No valid auth data in localStorage');
        clearAuthData();
      }
      setIsSubmitting(false);
    };

    initializeAuth();
  }, [dispatch]);

  const handleLogin = useCallback(async (credentials: LoginRequest) => {
    if (isSubmitting) return { success: false, error: 'Login already in progress' };
    
    setIsSubmitting(true);
    console.log('Attempting login for user:', credentials.username);
    
    try {
      // Dispatch the loginThunk
      await dispatch(loginThunk(credentials)).unwrap(); 
      // unwrap will automatically throw an error if the thunk is rejected
      
      console.log('Login thunk completed successfully');
      setIsSubmitting(false);
      return { success: true };
      
    } catch (error: any) {
      // The error here is the rejected value from loginThunk (handled by its extraReducer)
      console.error('Login failed (caught in useAuth):', error);
      setIsSubmitting(false);
      return { 
        success: false, 
        error: error || 'Login failed. Please check credentials.' // error is already the message from rejectWithValue
      };
    } 
    // No finally block needed for setIsSubmitting(false) as it's handled in both try and catch
  }, [dispatch, isSubmitting]);

  const handleRegister = useCallback(async (userData: RegisterRequest) => {
    if (isSubmitting) return { success: false, error: 'Registration already in progress' };
    
    try {
      setIsSubmitting(true);
      const result = await dispatch(register(userData)).unwrap();
      return { success: true, data: result };
    } catch (error: any) {
      console.error('Registration failed (caught in useAuth):', error);
      if (error instanceof Error && error.message.includes('Failed to fetch')) {
        return { 
          success: false, 
          error: 'Connection error. Please check your internet connection and try again.' 
        };
      }
      // The error here is already the processed error message from the register thunk
      return { 
        success: false, 
        error: error || 'Registration failed. Please try again.' 
      };
    } finally {
      setIsSubmitting(false);
    }
  }, [dispatch, isSubmitting]);

  const handleLogout = useCallback(() => {
    console.log('Logging out user');
    // Clear localStorage
    clearAuthData();
    
    // Update Redux state
    dispatch(logout());
  }, [dispatch]);
  
  const handleClearError = () => {
    dispatch(clearError());
  };
  
  // Updated to prevent unnecessary API calls and include better error handling
  const handleFetchCurrentUser = useCallback(async (options?: { force?: boolean }) => {
    // Check localStorage directly for token before proceeding
    const localStorageToken = localStorage.getItem(TOKEN_STORAGE_KEY);
    
    if (!localStorageToken) {
      console.log('No token in localStorage, aborting fetchCurrentUser');
      return;
    }
    
    // Don't fetch if:
    // 1. We're already loading
    // 2. We've already tried to fetch and no user was found
    // 3. We don't have a token in Redux state
    if (!token) {
      console.log('Skipping fetchCurrentUser due to missing redux token');
      return;
    }

    if (loading) {
      console.log('Skipping fetchCurrentUser because auth is loading');
      return;
    }

    if (options?.force && userFetchAttempted) {
      console.log('Skipping forced fetchCurrentUser because bootstrap sync already attempted');
      return;
    }

    if (!options?.force && userFetchAttempted && !user) {
      console.log('Skipping fetchCurrentUser due to conditions:', { 
        loading, 
        userFetchAttempted, 
        hasUser: !!user, 
        hasToken: !!token 
      });
      return;
    }
    
    try {
      console.log('Dispatching fetchCurrentUser');
      await dispatch(fetchCurrentUser(options)).unwrap();
      console.log('fetchCurrentUser successful');
    } catch (error) {
      console.error('Error in fetchCurrentUser:', error);
    }
  }, [dispatch, loading, token, user, userFetchAttempted]);
  
  const handleResetUserFetchAttempt = useCallback(() => {
    dispatch(resetUserFetchAttempt());
  }, [dispatch]);

  const handleGoogleLogin = useCallback(
    async (idToken: string, trialToken?: string | null) => {
      if (isSubmitting) return { success: false, error: 'Login already in progress' };

      setIsSubmitting(true);
      console.log('Attempting Google login');

      try {
        await dispatch(
          googleLoginThunk({
            idToken,
            ...(trialToken ? { trial_token: trialToken } : {}),
          })
        ).unwrap(); 
      
      console.log('Google login thunk completed successfully');
      setIsSubmitting(false);
      return { success: true };
      
    } catch (error: any) {
      console.error('Google login failed (caught in useAuth):', error);
      setIsSubmitting(false);
      return { 
        success: false, 
        error: error || 'Google login failed.'
      };
    } 
  }, [dispatch, isSubmitting]);
  
  return {
    user,
    token,
    loading,
    error,
    isSubmitting,
    isAuthenticated,
    login: handleLogin,
    googleLogin: handleGoogleLogin,
    register: handleRegister,
    logout: handleLogout,
    clearError: handleClearError,
    fetchCurrentUser: handleFetchCurrentUser,
    resetUserFetchAttempt: handleResetUserFetchAttempt
  };
}; 